import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { auth, db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { usePostHog } from 'posthog-js/react';
import { AdminViewProvider } from './contexts/AdminViewContext';
import NavigationBar from './components/NavigationBar';
import ImpersonationBar from './components/ImpersonationBar';
import HomePage from './pages/HomePage';
import CreatePage from './pages/CreatePage';
import ProfilePage from './pages/ProfilePage';
import PostDetailPage from './pages/PostDetailPage';
import ManagePostPage from './pages/ManagePostPage';
import SettingsPage from './pages/SettingsPage';
import EditProfilePage from './pages/EditProfilePage';
import DiamondsPage from './pages/DiamondsPage';
import AdminMenuPage from './pages/AdminMenuPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminNewsletterPage from './pages/AdminNewsletterPage';
import PromptManagementPage from './pages/PromptManagementPage';
import StravaWebhookAdminPage from './pages/StravaWebhookAdminPage';
import AdminDashboard from './pages/AdminDashboard';
import TestComparisonsListPage from './pages/TestComparisonsListPage';
import TestComparisonPage from './pages/TestComparisonPage';
import GpxViewerPage from './pages/GpxViewerPage';
import GpxMapPage from './pages/GpxMapPage';
import StravaAuthCallbackPage from './pages/StravaAuthCallbackPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import IOSDownloadPage from './pages/IOSDownloadPage';
import ChallengePage from './pages/ChallengePage';
import AutoUploadClaimPage from './pages/AutoUploadClaimPage';
import InviteLandingPage from './pages/InviteLandingPage';
import EventLandingPage from './pages/EventLandingPage';
import EventManagementPage from './pages/EventManagementPage';
import MapGenerationPage from './pages/MapGenerationPage';
import ZwiftFixPage from './pages/ZwiftFixPage';
import JoinMythicMapsModal from './components/JoinMythicMapsModal';
import './App.css';

const ZwiftTerrainExplorerPage = React.lazy(() => import('./pages/ZwiftTerrainExplorerPage'));

// Component to redirect from old /generate/activity route to new /create route
function GenerateActivityRedirect() {
  const { activityId } = useParams();
  return <Navigate to={`/create/${activityId}`} replace />;
}

// Shortcut used in auto-generated Strava captions (mythicmaps.ai/strava).
// Logged-in users land on the auto-upload settings section. Logged-out visitors
// (e.g. arriving from a fixed-map caption link on someone's Strava feed) go to the
// home page, which is now the Zwift fix funnel.
function StravaRedirect({ user }) {
  return <Navigate to={user ? '/settings#auto-upload' : '/'} replace />;
}

// Component to track page views
function PageTracker() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    if (posthog) {
      // Helper to get page name from pathname
      const getPageName = (pathname) => {
        if (pathname === '/') return 'Zwift Fix Home';
        if (pathname === '/art') return 'Art Feed';
        if (pathname === '/fix') return 'Zwift Fix';
        if (pathname === '/strava') return 'Strava Shortcut';
        if (pathname === '/challenge') return 'Challenge';
        if (pathname.startsWith('/challenge/')) return 'Challenge Detail';
        if (pathname === '/create') return 'Create';
        if (pathname.startsWith('/create/')) return 'Create Activity';
        if (pathname.startsWith('/generate/activity/')) return 'Generate Activity Redirect';
        if (pathname.startsWith('/profile/')) return 'Profile';
        if (pathname.startsWith('/post/')) return 'Post Detail';
        if (pathname.startsWith('/manage-post/')) return 'Manage Post';
        if (pathname === '/settings') return 'Settings';
        if (pathname === '/edit-profile') return 'Edit Profile';
        if (pathname === '/diamonds') return 'Diamonds';
        if (pathname === '/admin') return 'Admin Menu';
        if (pathname === '/admin/settings') return 'Admin Settings';
        if (pathname === '/admin/newsletter') return 'Newsletter Sending';
        if (pathname === '/admin/prompts') return 'Prompt Management';
        if (pathname === '/admin/strava-webhook') return 'Strava Webhook Admin';
        if (pathname === '/admin/dashboard') return 'Admin Dashboard';
        if (pathname === '/admin/test-comparisons') return 'Test Comparisons';
        if (pathname === '/admin/zwift-terrain') return 'Zwift Terrain Explorer';
        if (pathname.startsWith('/admin/test-comparison/')) return 'Test Comparison Detail';
        if (pathname === '/strava-auth-callback') return 'Strava Auth Callback';
        if (pathname === '/terms') return 'Terms of Service';
        if (pathname === '/privacy') return 'Privacy Policy';
        if (pathname === '/cookies') return 'Cookie Policy';
        if (pathname.startsWith('/ios-download/')) return 'iOS Download';
        return pathname;
      };

      posthog.capture('page_viewed', {
        page_name: getPageName(location.pathname),
        pathname: location.pathname,
        search: location.search,
        page_url: window.location.href,
      });
    }
  }, [location, posthog]);

  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [impersonationData, setImpersonationData] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Fetch user profile from Firestore to get correct photoURL
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Override photoURL with the one from Firestore if available
            if (userData.photoURL) {
              currentUser.photoURL = userData.photoURL;
            }
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }

        // Check if we're in impersonation mode
        const storedImpersonation = localStorage.getItem('impersonationData');
        if (storedImpersonation) {
          try {
            const data = JSON.parse(storedImpersonation);
            // Verify this is still a valid impersonation session
            if (data.isImpersonating && currentUser.uid === data.targetUser?.uid) {
              setImpersonationData(data);
              document.body.classList.add('impersonating');
            } else {
              // Clear invalid impersonation data
              localStorage.removeItem('impersonationData');
              document.body.classList.remove('impersonating');
            }
          } catch (e) {
            console.error('Error parsing impersonation data:', e);
            localStorage.removeItem('impersonationData');
          }
        }
      } else {
        setImpersonationData(null);
        document.body.classList.remove('impersonating');
      }
      setUser(currentUser);
      setLoadingAuth(false);
    });
    return () => {
      unsubscribe();
      document.body.classList.remove('impersonating');
    };
  }, []);

  if (loadingAuth) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading...</div>;
  }

  return (
    <AdminViewProvider>
      <Router>
        <PageTracker />
        <HomePageWrapper user={user} impersonationData={impersonationData} setImpersonationData={setImpersonationData} />
      </Router>
    </AdminViewProvider>
  );
}

// Picks the join-modal experience for the page the user is on.
// '/', '/maps', '/create', '/challenge' are single-product landing pages that
// already pitch their product, so connecting skips the modal and goes straight to
// Strava OAuth. '/art' (the gallery) keeps the art modal. Everything else
// (profile, post, diamonds…) gets the three-product overview.
function resolveJoinVariant(pathname) {
  if (pathname === '/art') return 'art';
  if (pathname === '/' || pathname === '/fix') return 'skip';
  if (pathname.startsWith('/maps') || pathname.startsWith('/create')) return 'skip';
  if (pathname.startsWith('/challenge')) return 'skip';
  return 'products';
}

// Wrapper to handle login callback
function HomePageWrapper({ user, impersonationData, setImpersonationData }) {
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [joinVariant, setJoinVariant] = React.useState('art');
  const [userCredits, setUserCredits] = React.useState(null);
  const [isLoadingCredits, setIsLoadingCredits] = React.useState(false);
  const creditsUnsubRef = React.useRef(null);
  const hasLoadedCreditsRef = React.useRef(false);
  const posthog = usePostHog();

  const handleLoginRequired = React.useCallback(() => {
    const variant = resolveJoinVariant(window.location.pathname);
    if (variant === 'skip') {
      // Landing pages already carry the pitch, so go straight to Strava OAuth.
      handleStravaConnect();
      return;
    }
    setJoinVariant(variant);
    setShowLoginModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAutoShowJoinModal = React.useCallback(() => {
    setJoinVariant('art');
    setShowLoginModal(true);
    posthog?.capture('join_modal_auto_opened', {
      user_authenticated: false,
    });
  }, [posthog]);

  const handleStravaButtonClicked = React.useCallback(() => {
    posthog?.capture('join_modal_strava_clicked');
  }, [posthog]);

  const handleLearnMoreClicked = React.useCallback(() => {
    posthog?.capture('join_modal_learn_more_clicked');
  }, [posthog]);

  const handleStravaConnect = () => {
    // Return the user to the page they started on after the OAuth round-trip.
    // Two exceptions: the Art Feed funnels into the Art Map page, and "/" maps
    // to /fix because once the user is logged in "/" redirects to the feed and
    // would skip the fix aha they signed up for.
    // The login modal is an overlay (it doesn't navigate), so window.location
    // is still the originating page at click time.
    try {
      const path = window.location.pathname;
      const returnUrl = (path === '/art' || path.startsWith('/art/')) ? '/maps'
        : (path === '/' ? '/fix' : path);
      localStorage.setItem('stravaOAuthReturnUrl', returnUrl);
    } catch (_) {
      /* storage unavailable; StravaAuthCallbackPage falls back to its default */
    }

    const STRAVA_CLIENT_ID = "159733";
    const STRAVA_SIGNIN_REDIRECT_URI = window.location.origin + "/strava-auth-callback";
    const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write,profile:read_all";
    
    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('stravaOAuthState', state);
    
    const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_SIGNIN_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=auto&state=${state}`;
    window.location.href = consentUrl;
  };

  const applyCreditsData = React.useCallback((data) => {
    if (!data || typeof data.remaining !== 'number') {
      setUserCredits(null);
      return;
    }
    setUserCredits(data.remaining);
  }, []);

  const loadMonthlyUsage = React.useCallback(async (overrideData) => {
    if (!user) {
      setUserCredits(null);
      return;
    }

    if (overrideData) {
      applyCreditsData(overrideData);
      setIsLoadingCredits(false);
      return;
    }

    setIsLoadingCredits(true);
    try {
      const getUserMonthlyUsageFunc = httpsCallable(functions, 'getUserMonthlyUsage');
      const result = await getUserMonthlyUsageFunc();
      applyCreditsData(result?.data || null);
    } catch (error) {
      console.error('Error loading monthly usage:', error);
      setUserCredits(null);
    } finally {
      setIsLoadingCredits(false);
    }
  }, [user, applyCreditsData]);

  React.useEffect(() => {
    return () => {
      if (creditsUnsubRef.current) {
        creditsUnsubRef.current();
        creditsUnsubRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (creditsUnsubRef.current) {
      creditsUnsubRef.current();
      creditsUnsubRef.current = null;
    }

    if (!user) {
      setUserCredits(null);
      setIsLoadingCredits(false);
      hasLoadedCreditsRef.current = false;
      return;
    }

    // Prevent duplicate loads on React Strict Mode double-mounting
    if (hasLoadedCreditsRef.current && user?.uid === hasLoadedCreditsRef.current) {
      return;
    }
    hasLoadedCreditsRef.current = user.uid;

    const userDocRef = doc(db, 'users', user.uid);
    creditsUnsubRef.current = onSnapshot(userDocRef, (snapshot) => {
      if (!snapshot.exists()) {
        void loadMonthlyUsage();
        return;
      }
      const data = snapshot.data();
      const cached = data?.calculatedCredits;
      if (cached && typeof cached.remaining === 'number') {
        applyCreditsData(cached);
        setIsLoadingCredits(false);
      }
    });

    void loadMonthlyUsage();
  }, [user, loadMonthlyUsage, applyCreditsData]);

  return (
    <>
      {/* Show impersonation bar when in impersonation mode */}
      <ImpersonationBar
        isImpersonating={!!impersonationData?.isImpersonating}
        targetUser={impersonationData?.targetUser}
        onEndImpersonation={() => {
          setImpersonationData(null);
          document.body.classList.remove('impersonating');
        }}
      />

      <div className="App">
        <NavigationBar
          user={user}
          onLoginRequired={handleLoginRequired}
          userCredits={userCredits}
          isLoadingCredits={isLoadingCredits}
          onCreditsUpdated={loadMonthlyUsage}
        />
        <main className="main-content">
          <Routes>
            {/* Audience-dependent home: logged-out visitors get the Zwift fix LP (the
                acquisition/SEO surface); logged-in users get the feed. The fix LP stays
                reachable for members at /fix (Explore menu); logged-out /fix redirects
                to the canonical "/". */}
            <Route path="/" element={user
              ? <Navigate to="/art" replace />
              : <ZwiftFixPage user={user} onLoginRequired={handleLoginRequired} onStravaConnect={handleStravaConnect} onCreditsUpdated={loadMonthlyUsage} />} />
            <Route path="/art" element={<HomePage user={user} onLoginRequired={handleLoginRequired} onAutoShowLoginModal={handleAutoShowJoinModal} onCreditsUpdated={loadMonthlyUsage} />} />
            <Route path="/fix" element={user
              ? <ZwiftFixPage user={user} onLoginRequired={handleLoginRequired} onStravaConnect={handleStravaConnect} onCreditsUpdated={loadMonthlyUsage} />
              : <Navigate to="/" replace />} />
            <Route path="/maps" element={<MapGenerationPage user={user} onLoginRequired={handleLoginRequired} onCreditsUpdated={loadMonthlyUsage} />} />
            <Route path="/challenge" element={<ChallengePage user={user} onLoginRequired={handleLoginRequired} onCreditsUpdated={loadMonthlyUsage} />} />
            <Route path="/challenge/:configId" element={<GpxMapPage user={user} />} />
            <Route path="/create" element={<CreatePage user={user} onCreditsUpdated={loadMonthlyUsage} />} />
            <Route path="/create/:activityId" element={<CreatePage user={user} onCreditsUpdated={loadMonthlyUsage} />} />
            <Route path="/generate/activity/:activityId" element={<GenerateActivityRedirect />} />
            <Route path="/profile" element={<ProfilePage user={user} />} />
            <Route path="/profile/:uid" element={<ProfilePage user={user} />} />
            <Route path="/post/:postId" element={<PostDetailPage user={user} onLoginRequired={handleLoginRequired} />} />
            <Route path="/manage-post/:postId" element={<ManagePostPage user={user} onCreditsUpdated={loadMonthlyUsage} />} />
            <Route path="/settings" element={<SettingsPage user={user} />} />
            <Route path="/strava" element={<StravaRedirect user={user} />} />
            <Route path="/edit-profile" element={<EditProfilePage user={user} />} />
            <Route path="/diamonds" element={<DiamondsPage user={user} onCreditsUpdated={loadMonthlyUsage} />} />
            <Route path="/admin-menu" element={<AdminMenuPage user={user} />} />
            <Route path="/admin-settings" element={<AdminSettingsPage user={user} />} />
            <Route path="/admin/newsletter" element={<AdminNewsletterPage user={user} />} />
            <Route path="/prompt-management" element={<PromptManagementPage user={user} />} />
            <Route path="/strava-webhook-admin" element={<StravaWebhookAdminPage user={user} />} />
            <Route path="/admin/dashboard" element={<AdminDashboard user={user} />} />
            <Route path="/admin/test-comparisons" element={<TestComparisonsListPage user={user} />} />
            <Route path="/admin/compare/:parentId" element={<TestComparisonPage user={user} />} />
            <Route
              path="/admin/zwift-terrain"
              element={(
                <React.Suspense fallback={<div style={{ textAlign: 'center', marginTop: '50px' }}>Loading terrain explorer...</div>}>
                  <ZwiftTerrainExplorerPage user={user} />
                </React.Suspense>
              )}
            />
            <Route path="/tools/gpx-viewer" element={<GpxViewerPage user={user} />} />
            <Route path="/map/:configId" element={<GpxMapPage user={user} />} />
            <Route path="/strava-auth-callback" element={<StravaAuthCallbackPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/ios-download/:requestId" element={<IOSDownloadPage />} />
            <Route path="/auto-upload/:token" element={<AutoUploadClaimPage user={user} />} />
            <Route path="/invite/:inviteId" element={<InviteLandingPage user={user} />} />
            <Route path="/event/:eventId" element={<EventLandingPage user={user} onCreditsUpdated={loadMonthlyUsage} />} />
            <Route path="/admin/events" element={<EventManagementPage user={user} />} />
          </Routes>
        </main>
      </div>

      {/* Global Join Modal (replaces previous bare login modal) */}
      <JoinMythicMapsModal
        open={showLoginModal}
        variant={joinVariant}
        onDismiss={() => setShowLoginModal(false)}
        onConnect={handleStravaConnect}
        onStravaClicked={handleStravaButtonClicked}
        onLearnMore={handleLearnMoreClicked}
      />
    </>
  );
}

export default App;
